import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, ExternalLink } from "lucide-react";

export default function Findings() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["findings", q],
    queryFn: async () => (await api.get(`/findings?q=${encodeURIComponent(q)}`)).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => editId ? api.put(`/findings/${editId}`, payload) : api.post("/findings", payload),
    onSuccess: () => { toast.success(editId ? "Finding updated" : "Finding logged"); qc.invalidateQueries({ queryKey: ["findings"] }); setOpen(false); setForm({}); setEditId(null); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/findings/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["findings"] }); },
  });

  const openNew = () => { setForm({ date: new Date().toISOString().slice(0, 10) }); setEditId(null); setOpen(true); };
  const openEdit = (row) => { setForm({ ...row }); setEditId(row.id); setOpen(true); };
  const submit = () => { if (!form.title?.trim()) return toast.error("Title is required"); saveMut.mutate(form); };

  const items = data || [];

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Research Findings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Log new findings or search results discovered during research.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="add-finding-btn"><Plus className="h-4 w-4 mr-2" />Log Finding</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "New"} Finding</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="finding-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Tags</Label><Input value={form.tags || ""} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="comma, separated" data-testid="finding-tags" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="finding-date" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Source URL</Label><Input value={form.source_url || ""} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://…" data-testid="finding-source" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Content</Label><Textarea rows={5} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="finding-content" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saveMut.isPending} data-testid="finding-save">{saveMut.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search findings…" className="pl-9" data-testid="finding-search" />
      </div>

      <div className="space-y-3">
        {isLoading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        {!isLoading && items.length === 0 && <div className="border border-dashed border-border rounded-lg p-16 text-center text-sm text-muted-foreground">No findings logged yet.</div>}
        {!isLoading && items.map((f) => (
          <div key={f.id} className="border border-border rounded-lg p-4 hover:bg-muted/40 transition-colors duration-200" data-testid={`finding-row-${f.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{f.title}</span>
                  {f.date && <span className="text-xs text-muted-foreground font-mono">{f.date}</span>}
                </div>
                {f.tags && <div className="flex flex-wrap gap-1 mt-1.5">{f.tags.split(",").map((t) => t.trim()).filter(Boolean).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>}
                {f.content && <p className="text-sm text-muted-foreground mt-2 break-words whitespace-pre-wrap">{f.content}</p>}
                {f.source_url && <a href={f.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">{f.source_url} <ExternalLink className="h-3 w-3" /></a>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(f)} data-testid={`finding-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`finding-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete finding?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delMut.mutate(f.id)} data-testid="finding-confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
